// /Users/shawn/💼_WORKSPACE/Projects/FixPriceAI-Desktop/src/services/AIVisionService.js
// Purpose: AI-powered construction image analysis using Google Gemini and Groq
// NOT for: Simple image viewing - this analyzes and estimates costs

import { API_KEYS, AI_MODELS, AI_ENDPOINTS, POLISH_CONSTRUCTION_COSTS } from '../config/apiKeys.js';

class AIVisionService {
  constructor() {
    this.primaryAI = 'gemini'; // Use Gemini as primary
    this.backupAI = 'groq'; // Groq as backup (free unlimited)
    this.isOnline = navigator.onLine;

    // Listen for online/offline changes
    window.addEventListener('online', () => { this.isOnline = true; });
    window.addEventListener('offline', () => { this.isOnline = false; });
  }

  /**
   * Analyze construction image and estimate costs
   * Main entry point for FixPriceAI functionality
   */
  async analyzeConstructionImage(imageFile, options = {}) {
    const {
      location = 'Warszawa',
      projectType = 'remont',
      analysisDepth = 'detailed'
    } = options;

    try {
      console.log('🔍 Starting AI analysis of construction image...');

      // Convert image to base64
      const imageBase64 = await this.convertImageToBase64(imageFile);

      // Try primary AI (Gemini) first
      let analysisResult;
      try {
        analysisResult = await this.analyzeWithGemini(imageBase64, location, projectType, analysisDepth);
        console.log('✅ Gemini analysis successful');
      } catch (error) {
        console.warn('⚠️ Gemini failed, trying Groq backup:', error.message);
        analysisResult = await this.analyzeWithGroq(imageBase64, location, projectType, analysisDepth);
        console.log('✅ Groq backup analysis successful');
      }

      // Enhance with Polish market data
      const enhancedResult = this.enhanceWithPolishMarketData(analysisResult, location);

      console.log('🎯 Final analysis result:', enhancedResult);
      return enhancedResult;

    } catch (error) {
      console.error('❌ AI analysis failed:', error);
      throw new Error(`Analiza AI nie powiodła się: ${error.message}`);
    }
  }

  /**
   * Analyze image using Google Gemini Vision
   */
  async analyzeWithGemini(imageBase64, location, projectType, analysisDepth) {
    const prompt = this.buildAnalysisPrompt(location, projectType, analysisDepth);

    const requestBody = {
      contents: [{
        parts: [
          { text: prompt },
          {
            inline_data: {
              mime_type: "image/jpeg",
              data: imageBase64
            }
          }
        ]
      }]
    };

    const response = await fetch(
      `${AI_ENDPOINTS.GEMINI}/${AI_MODELS.GEMINI.text}:generateContent?key=${API_KEYS.GOOGLE_GEMINI}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      }
    );

    if (!response.ok) {
      throw new Error(`Gemini API error: ${response.status}`);
    }

    const data = await response.json();
    const analysisText = data.candidates[0].content.parts[0].text;

    return this.parseAnalysisResult(analysisText, 'gemini');
  }

  /**
   * Analyze image using Groq as backup (text-only analysis)
   */
  async analyzeWithGroq(imageBase64, location, projectType, analysisDepth) {
    // Since Groq doesn't support vision, we'll use a text-based approach
    // with intelligent construction estimation

    const prompt = `
Jesteś ekspertem od kosztów budowlanych w Polsce.
Użytkownik przesłał zdjęcie ze swojego ${projectType} w lokalizacji ${location}.

Przeanalizuj typowe prace remontowe w polskich mieszkaniach i podaj szczegółową wycenę:

1. IDENTYFIKACJA PRAC:
- Malowanie ścian i sufitu
- Wymiana/naprawa podłogi
- Prace elektryczne
- Prace hydrauliczne
- Gładzie ścian
- Wymiana okien/drzwi

2. WYCENA (PLN, ${location}):
- Podaj koszty materiałów i robocizny
- Uwzględnij stawki regionalne
- Dodaj 10-20% bufor na nieprzewidziane

3. HARMONOGRAM:
- Czas realizacji w dniach
- Kolejność prac

Format odpowiedzi: JSON z szczegółową wycenią.
`;

    const response = await fetch(AI_ENDPOINTS.GROQ, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${API_KEYS.GROQ}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: AI_MODELS.GROQ.text,
        messages: [
          {
            role: 'system',
            content: 'Jesteś profesjonalnym rzeczoznawcą budowlanym z 15-letnim doświadczeniem na polskim rynku.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.3,
        max_tokens: 2000
      })
    });

    if (!response.ok) {
      throw new Error(`Groq API error: ${response.status}`);
    }

    const data = await response.json();
    const analysisText = data.choices[0].message.content;

    return this.parseAnalysisResult(analysisText, 'groq');
  }

  /**
   * Build comprehensive analysis prompt for construction cost estimation
   */
  buildAnalysisPrompt(location, projectType, analysisDepth) {
    return `
Jesteś ekspertem od kosztów budowlanych w Polsce z 20-letnim doświadczeniem.
Przeanalizuj to zdjęcie z ${projectType} w ${location} i podaj szczegółową wycenę.

INSTRUKCJE ANALIZY:
1. IDENTYFIKUJ dokładnie co widać na zdjęciu:
   - Rodzaj pomieszczeń
   - Stan ścian, podłogi, sufitu
   - Widoczne instalacje
   - Elementy do naprawy/wymiany

2. OSZACUJ POWIERZCHNIĘ:
   - Użyj wizualnych wskazówek (meble, drzwi jako odniesienia)
   - Podaj przybliżone wymiary

3. WYCENA PRAC (aktualne ceny ${location} 2025):
   - Materiały (sklepy budowlane: Castorama, Leroy Merlin)
   - Robocizna (lokalne stawki)
   - Koszty dodatkowe (wywóz gruzu, itp.)

4. HARMONOGRAM:
   - Kolejność prac
   - Czas realizacji
   - Punkty krytyczne

ODPOWIEDŹ w formacie JSON:
{
  "identyfikacja": {
    "typ_pomieszczenia": "",
    "powierzchnia_m2": 0,
    "stan_ogolny": "",
    "glowne_problemy": []
  },
  "prace_do_wykonania": [
    {
      "nazwa": "",
      "opis": "",
      "powierzchnia": 0,
      "jednostka": "",
      "koszt_materialow": 0,
      "koszt_robocizny": 0,
      "czas_dni": 0
    }
  ],
  "podsumowanie": {
    "koszt_calkowity": 0,
    "czas_calkowity_dni": 0,
    "zalecenia": []
  }
}

WAŻNE: Używaj realnych polskich cen z 2025 roku!
`;
  }

  /**
   * Parse AI response and structure the result
   */
  parseAnalysisResult(analysisText, aiProvider) {
    try {
      // Try to extract JSON from the response
      const jsonMatch = analysisText.match(/\{[\s\S]*\}/);

      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        return {
          ...parsed,
          metadata: {
            provider: aiProvider,
            timestamp: new Date().toISOString(),
            confidence: aiProvider === 'gemini' ? 0.9 : 0.8
          }
        };
      } else {
        // Fallback: create structured result from text
        return this.createFallbackStructure(analysisText, aiProvider);
      }
    } catch (error) {
      console.warn('Failed to parse JSON, creating fallback structure');
      return this.createFallbackStructure(analysisText, aiProvider);
    }
  }

  /**
   * Create fallback structure when JSON parsing fails
   */
  createFallbackStructure(analysisText, aiProvider) {
    return {
      identyfikacja: {
        typ_pomieszczenia: "Pokój do remontu",
        powierzchnia_m2: 20,
        stan_ogolny: "Wymaga remontu",
        glowne_problemy: ["Malowanie", "Podłoga", "Instalacje"]
      },
      prace_do_wykonania: [
        {
          nazwa: "Malowanie ścian",
          opis: "Przygotowanie i malowanie ścian farbą emulsyjną",
          powierzchnia: 50,
          jednostka: "m²",
          koszt_materialow: 800,
          koszt_robocizny: 1500,
          czas_dni: 3
        },
        {
          nazwa: "Wymiana podłogi",
          opis: "Demontaż starej podłogi, montaż paneli",
          powierzchnia: 20,
          jednostka: "m²",
          koszt_materialow: 1200,
          koszt_robocizny: 800,
          czas_dni: 2
        }
      ],
      podsumowanie: {
        koszt_calkowity: 4300,
        czas_calkowity_dni: 5,
        zalecenia: [
          "Rozpocznij od prac przy ścianach",
          "Podłogę rób na końcu",
          "Dodaj 15% bufor na nieprzewidziane koszty"
        ]
      },
      analysis_text: analysisText,
      metadata: {
        provider: aiProvider,
        timestamp: new Date().toISOString(),
        confidence: 0.7,
        fallback: true
      }
    };
  }

  /**
   * Enhance AI result with Polish market data and regional pricing
   */
  enhanceWithPolishMarketData(analysisResult, location) {
    const regionalMultiplier = this.getRegionalMultiplier(location);

    // Adjust costs based on region
    if (analysisResult.prace_do_wykonania) {
      analysisResult.prace_do_wykonania = analysisResult.prace_do_wykonania.map(praca => ({
        ...praca,
        koszt_materialow: Math.round(praca.koszt_materialow * regionalMultiplier),
        koszt_robocizny: Math.round(praca.koszt_robocizny * regionalMultiplier),
        koszt_region_info: `Ceny dostosowane do regionu ${location}`
      }));
    }

    // Recalculate total
    if (analysisResult.podsumowanie) {
      const nowyKoszto = analysisResult.prace_do_wykonania.reduce(
        (suma, praca) => suma + praca.koszt_materialow + praca.koszt_robocizny,
        0
      );

      analysisResult.podsumowanie.koszt_calkowity = Math.round(nowyKoszto);
      analysisResult.podsumowanie.koszt_region = location;
      analysisResult.podsumowanie.regional_multiplier = regionalMultiplier;
    }

    // Add market insights
    analysisResult.market_insights = {
      location: location,
      last_updated: new Date().toISOString(),
      price_trend: "Stabilne ceny w 2025",
      seasonal_note: this.getSeasonalNote(),
      supplier_recommendations: [
        "Castorama - dobry stosunek jakości do ceny",
        "Leroy Merlin - szeroki wybór materiałów",
        "Lokalne sklepy - często lepsze ceny robocizny"
      ]
    };

    return analysisResult;
  }

  /**
   * Get regional price multiplier for different Polish cities
   */
  getRegionalMultiplier(location) {
    const multipliers = {
      'Warszawa': 1.15,
      'Kraków': 1.10,
      'Gdańsk': 1.08,
      'Wrocław': 1.05,
      'Poznań': 1.05,
      'Łódź': 0.95,
      'Katowice': 1.00,
      'Lublin': 0.90,
      'Białystok': 0.85,
      'Rzeszów': 0.90
    };

    return multipliers[location] || 1.00; // Default multiplier
  }

  /**
   * Get seasonal construction note
   */
  getSeasonalNote() {
    const month = new Date().getMonth();

    if (month >= 10 || month <= 2) {
      return "Sezon zimowy - dobre ceny, dostępność fachowców";
    } else if (month >= 3 && month <= 5) {
      return "Sezon wiosenny - wzrost cen, planuj wcześniej";
    } else {
      return "Sezon letni - najwyższe ceny, długie kolejki";
    }
  }

  /**
   * Convert uploaded image to base64
   */
  async convertImageToBase64(imageFile) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1]; // Remove data:image/jpeg;base64, prefix
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(imageFile);
    });
  }

  /**
   * Get service status and capabilities
   */
  getServiceStatus() {
    return {
      online: this.isOnline,
      primary_ai: this.primaryAI,
      backup_ai: this.backupAI,
      capabilities: [
        'Analiza obrazów budowlanych',
        'Wycena kosztów materiałów',
        'Kalkulacja robocizny',
        'Dostosowanie regionalne cen',
        'Harmonogram prac',
        'Rekomendacje dostawców'
      ],
      supported_formats: ['jpg', 'jpeg', 'png', 'webp'],
      max_image_size: '10MB'
    };
  }
}

export default AIVisionService;